// =====================================================
// 📊 OPERATIONAL DASHBOARD - MÉTRICAS ADMIN
// =====================================================

import { useState, useEffect } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { useTheme } from '../../contexts/ThemeContext'
import { useNavigate } from 'react-router-dom'
import {
  Ticket, AlertTriangle, Clock, CheckCircle, 
  TrendingUp, Search, MessageSquare, Zap,
  AlertCircle, Activity, BarChart3, Settings,
  ArrowUp, ArrowDown, Minus
} from 'lucide-react'
import axios from 'axios'

// 🌐 DETECCIÓN AUTOMÁTICA DE ENTORNO
const getApiUrl = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  
  const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
  
  if (isProduction) {
    const protocol = window.location.protocol
    const host = window.location.hostname
    const port = window.location.port || '4343'
    return `${protocol}//${host}:${port}/api`
  }
  
  return 'http://localhost:3001/api'
}

const API_URL = getApiUrl()

console.log('🔍 [API-CONFIG] URL detectada:', API_URL)

interface TicketStats {
  by_status: { status: string; count: number }[]
  by_priority: { priority: string; count: number }[]
  averages: { avg_rating: number; avg_resolution_hours: number }
  recent_tickets: any[]
}

interface SearchStats {
  total_searches: number
  cache_hits: number
  avg_processing_time: number
  unique_queries: number
}

interface PopularQuery {
  query_text: string
  search_count: number
}

interface MotorStat {
  motor: string
  total_tickets: number
  high_priority_count: number
  open_tickets: number
  closed_tickets: number
  avg_resolution_hours: number
  last_ticket_date: string
}

export default function OperationalDashboard() {
  const { user } = useAuthStore()
  const { isDark } = useTheme()
  const navigate = useNavigate()
  
  const [ticketStats, setTicketStats] = useState<TicketStats | null>(null)
  const [searchStats, setSearchStats] = useState<SearchStats | null>(null)
  const [popularQueries, setPopularQueries] = useState<PopularQuery[]>([])
  const [motorStats, setMotorStats] = useState<MotorStat[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
    const interval = setInterval(loadDashboardData, 60000) // Actualizar cada minuto
    return () => clearInterval(interval)
  }, [])

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token')
      const config = { headers: { Authorization: `Bearer ${token}` } }

      const [ticketsRes, searchesRes, popularRes, motorsRes] = await Promise.all([
        axios.get(`${API_URL}/tickets/stats/dashboard`, config),
        axios.get(`${API_URL}/analytics/searches`, config).catch(() => ({ 
          data: { total_searches: 0, cache_hits: 0, avg_processing_time: 0, unique_queries: 0 } 
        })),
        axios.get(`${API_URL}/analytics/popular`, config).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/tickets/stats/motors`, config).catch(() => ({ data: { motor_stats: [] } }))
      ])

      // ✅ NULL-SAFETY: Validar y normalizar datos
      console.log('📊 [OPERATIONAL] Datos recibidos:', {
        tickets: ticketsRes.data,
        searches: searchesRes.data,
        popular: popularRes.data,
        motors: motorsRes.data
      })

      setTicketStats(ticketsRes.data.stats || ticketsRes.data)
      
      // Normalizar searchStats con valores seguros
      const searchData = searchesRes.data || {}
      setSearchStats({
        total_searches: searchData.total_searches || 0,
        cache_hits: searchData.cache_hits || 0,
        avg_processing_time: searchData.avg_processing_time || 0,
        unique_queries: searchData.unique_queries || 0
      })
      
      // Manejar popularQueries - puede venir como array directo o en propiedad
      const popularData = Array.isArray(popularRes.data) 
        ? popularRes.data 
        : (popularRes.data?.queries || popularRes.data?.popular_queries || [])
      setPopularQueries(popularData.slice(0, 5))
      
      // Manejar motorStats - puede venir como array directo o en propiedad
      const motorsData = Array.isArray(motorsRes.data)
        ? motorsRes.data
        : (motorsRes.data?.motor_stats || motorsRes.data?.motors || [])
      setMotorStats(motorsData.slice(0, 5))
      
    } catch (error) {
      console.error('❌ Error cargando datos dashboard:', error)
      console.error('📋 Detalles del error:', error.response?.data || error.message)
    } finally {
      setLoading(false)
    }
  }

  const getStatusCount = (status: string) => 
    ticketStats?.by_status.find(s => s.status === status)?.count || 0

  const getPriorityCount = (priority: string) =>
    ticketStats?.by_priority.find(p => p.priority === priority)?.count || 0

  const totalTickets = ticketStats?.by_status.reduce((sum, s) => sum + s.count, 0) || 0
  const openTickets = getStatusCount('open') + getStatusCount('in_progress')
  const closedTickets = getStatusCount('closed') + getStatusCount('resolved')

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard Operacional
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Vista en tiempo real del sistema · Actualizado hace {new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <button 
          onClick={loadDashboardData}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <Activity className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {/* KPIs Principales - 4 COLUMNAS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Total Tickets */}
        <div className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/tickets')}>
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Ticket className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Total</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{totalTickets}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Tickets Totales</p>
        </div>

        {/* Tickets Abiertos */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 p-6 rounded-xl border border-orange-200 dark:border-orange-800 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/tickets?status=open')}>
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <span className="text-xs font-medium text-orange-700 dark:text-orange-400">Activos</span>
          </div>
          <p className="text-3xl font-bold text-orange-900 dark:text-orange-100 mb-1">{openTickets}</p>
          <p className="text-sm text-orange-700 dark:text-orange-300">Abiertos/En Progreso</p>
        </div>

        {/* Tickets Cerrados */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-6 rounded-xl border border-green-200 dark:border-green-800 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/tickets?status=closed')}>
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <span className="text-xs font-medium text-green-700 dark:text-green-400">Completados</span>
          </div>
          <p className="text-3xl font-bold text-green-900 dark:text-green-100 mb-1">{closedTickets}</p>
          <p className="text-sm text-green-700 dark:text-green-300">Resueltos/Cerrados</p>
        </div>

        {/* Tiempo Promedio */}
        <div className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Promedio</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
            {Math.round(ticketStats?.averages.avg_resolution_hours || 0)}h
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Tiempo Resolución</p>
        </div>
      </div>

      {/* GRID PRINCIPAL - 2 COLUMNAS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* COLUMNA IZQUIERDA - 2/3 */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Distribución por Prioridad */}
          <div className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Distribución por Prioridad
            </h2>
            
            <div className="space-y-3">
              {ticketStats?.by_priority.map((priority) => {
                const percentage = totalTickets > 0 ? (priority.count / totalTickets) * 100 : 0
                const colors = {
                  high: { bg: 'from-red-400 to-red-500', text: 'text-red-700 dark:text-red-300', label: 'Alta' },
                  medium: { bg: 'from-yellow-400 to-yellow-500', text: 'text-yellow-700 dark:text-yellow-300', label: 'Media' },
                  low: { bg: 'from-green-400 to-green-500', text: 'text-green-700 dark:text-green-300', label: 'Baja' }
                }[priority.priority] || { bg: 'from-gray-400 to-gray-500', text: 'text-gray-700 dark:text-gray-300', label: priority.priority }
                
                return (
                  <div key={priority.priority}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium ${colors.text}`}>{colors.label}</span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {priority.count} ({percentage.toFixed(0)}%)
                      </span>
                    </div>
                    <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r ${colors.bg} rounded-full transition-all`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Cache Performance */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 rounded-xl border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                🚀 Performance del Sistema
              </h2>
              <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Cache Hit Rate</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {searchStats && searchStats.total_searches > 0 
                    ? ((searchStats.cache_hits / searchStats.total_searches) * 100).toFixed(1) 
                    : '0.0'}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Tiempo Promedio</p>
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {searchStats?.avg_processing_time?.toFixed(2) || '0.00'}s
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* COLUMNA DERECHA - 1/3 */}
        <div className="space-y-6">
          
          {/* Búsquedas Populares */}
          <div className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Consultas Frecuentes
              </h2>
              <Search className="w-5 h-5 text-gray-400" />
            </div>
            
            <div className="space-y-3">
              {popularQueries.length > 0 ? popularQueries.map((query, index) => (
                <div key={index} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-xs font-bold text-blue-600 dark:text-blue-400">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {query.query_text}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {query.search_count} búsquedas
                    </p>
                  </div>
                </div>
              )) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  No hay consultas registradas
                </p>
              )}
            </div>

            <button 
              onClick={() => navigate('/dashboard/chat')}
              className="w-full mt-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            >
              Ir al Chat
            </button>
          </div>
        </div>
      </div>

      {/* SECCIÓN CRÍTICA: MOTORES PROBLEMÁTICOS */}
      {motorStats.length > 0 && (
        <div className="bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 dark:from-red-900/10 dark:via-orange-900/10 dark:to-yellow-900/10 p-6 rounded-xl border-2 border-red-200 dark:border-red-800">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  ⚠️ Motores con Mayor Incidencia
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Identificación de problemas sistémicos por motor
                </p>
              </div>
            </div>
            <button 
              onClick={() => navigate('/dashboard/tickets?groupBy=motor')}
              className="px-4 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              Ver Análisis Completo
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {motorStats.map((motor, index) => {
              const criticalityScore = (motor.high_priority_count * 2) + motor.open_tickets
              const isCritical = criticalityScore > 5
              
              return (
                <div 
                  key={motor.motor}
                  onClick={() => navigate(`/dashboard/tickets?motor=${motor.motor}`)}
                  className={`p-5 rounded-xl border-2 transition-all cursor-pointer ${
                    isCritical
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 hover:shadow-lg hover:shadow-red-200 dark:hover:shadow-red-900/50'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                        index === 0 ? 'bg-red-500 text-white' :
                        index === 1 ? 'bg-orange-500 text-white' :
                        index === 2 ? 'bg-yellow-500 text-white' :
                        'bg-gray-500 text-white'
                      }`}>
                        {index + 1}
                      </span>
                      <h3 className="font-bold text-gray-900 dark:text-white truncate">
                        {motor.motor}
                      </h3>
                    </div>
                    {isCritical && (
                      <span className="px-2 py-1 text-xs font-bold text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 rounded-full">
                        CRÍTICO
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Total Tickets</span>
                      <span className="font-bold text-gray-900 dark:text-white">{motor.total_tickets}</span>
                    </div>
                    
                    {motor.high_priority_count > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Alta Prioridad
                        </span>
                        <span className="font-bold text-red-600 dark:text-red-400">{motor.high_priority_count}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-orange-600 dark:text-orange-400">Abiertos</span>
                      <span className="font-semibold text-orange-600 dark:text-orange-400">{motor.open_tickets}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-600 dark:text-green-400">Resueltos</span>
                      <span className="font-semibold text-green-600 dark:text-green-400">{motor.closed_tickets}</span>
                    </div>

                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 dark:text-gray-400">Tiempo Promedio</span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {Math.round(motor.avg_resolution_hours)}h
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Indicador visual de criticidad */}
                  <div className="mt-3 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all ${
                        isCritical ? 'bg-gradient-to-r from-red-500 to-red-600' :
                        motor.open_tickets > 3 ? 'bg-gradient-to-r from-orange-400 to-orange-500' :
                        'bg-gradient-to-r from-green-400 to-green-500'
                      }`}
                      style={{ width: `${Math.min((criticalityScore / 10) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>

          {motorStats.length > 3 && (
            <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                💡 <strong>Recomendación:</strong> Los motores marcados como CRÍTICO requieren atención inmediata. 
                Considera análisis de causa raíz para estos modelos.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Estado del Sistema */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Sistema Operacional
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Todos los servicios funcionando correctamente
              </p>
            </div>
          </div>
          <button 
            onClick={() => navigate('/dashboard/admin')}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <Settings className="w-4 h-4" />
            Panel Admin
          </button>
        </div>
      </div>
    </div>
  )
}